/* ────────────────────────────────────────────────────────────
 * CRM Admin navigation — the single source of truth.
 *
 * It drives three things so they can never drift apart:
 *   1. the short "CRM Admin" block in the sidebar (one link per group,
 *      built in sidebarMenuConfig.js),
 *   2. the tab strip CrmPage renders under every page header
 *      (Enquiry > Call | Web | Whatsapp | Chat Bot ...), and
 *   3. the "+ Add" menu in the page header.
 *
 * Sub-screens used to be ~50 nested sidebar rows; the mobile app shows
 * them as tabs inside a screen, which is what the web now does too.
 *
 * `matches` lists the detail / create routes that live under a tab, so
 * that tab (and the sidebar item) stays highlighted on those pages and
 * CrmPage knows to show a back button there.
 *
 * `countKey` is the admin_counts field that holds the tab's total
 * (flat, "inquiry" spelling, Web = `_email_`); CrmPage shows it on
 * the tab. Tabs without one show no number.
 *
 * `hidden: true` keeps a tab's routes registered and resolving — so a
 * bookmark or a direct link still lands on a page that knows which group it
 * belongs to — while leaving it out of the tab strip. It marks the screens
 * the mobile app doesn't offer, which QA reads as web/mobile drift: General
 * Reports showed 7 tabs against mobile's 4, and Outgoing offered Email
 * Promotion and Offers & Deals that mobile has no equivalent for. Deleting
 * them outright would strand their routes with no group, so they are hidden
 * rather than removed.
 *
 * `permission` is the CRM > Admin permission name that gates a group or a
 * single tab (Phase 3 "CRM Permission" sheet - see crmPermissions.js). A
 * group's own name gates its sidebar row; a tab's name gates that tab in the
 * strip. Entries without one stay ungated, exactly as before. Keeping them
 * here means the sidebar row, the tab strip and the "+ Add" menu enforce the
 * same gate instead of three copies drifting apart.
 * ──────────────────────────────────────────────────────────── */

import { CRM_PERM } from './crmPermissions';

export const CRM_NAV = [
  {
    key: 'calls',
    label: 'Recent Calls',
    icon: 'calls',
    permission: CRM_PERM.VIEW_RECENT_CALLS,
    tabs: [{ label: 'Recent Calls', to: '/RecentCalls', permission: CRM_PERM.VIEW_RECENT_CALLS }],
  },
  {
    key: 'dashboard',
    label: 'CRM Dashboard',
    icon: 'dashboard',
    permission: CRM_PERM.VIEW_CRM_DASHBOARD,
    tabs: [{ label: 'Dashboard', to: '/CRMAdminDashboard', permission: CRM_PERM.VIEW_CRM_DASHBOARD }],
  },
  {
    key: 'leads',
    label: 'Leads',
    icon: 'leads',
    permission: CRM_PERM.VIEW_RECENT_LEADS,
    tabs: [
      {
        label: 'Lead Home',
        to: '/CRMLeadHome',
        matches: ['/CRMLeadDetails', '/CRMLeadsByUser'],
        permission: CRM_PERM.VIEW_RECENT_LEADS,
      },
      {
        label: 'Lead Search',
        to: '/CRMLeadSearch',
        matches: ['/CRMLeadFilter'],
        permission: CRM_PERM.VIEW_RECENT_LEADS,
      },
    ],
  },
  {
    key: 'enquiry',
    label: 'Enquiry',
    icon: 'enquiry',
    permission: CRM_PERM.VIEW_ENQUIRY,
    tabs: [
      {
        label: 'Call',
        to: '/CRMEnquiryCall',
        countKey: 'inquiry_call_count',
        permission: CRM_PERM.VIEW_ENQUIRY_CALL,
      },
      {
        label: 'Web',
        to: '/CRMEnquiryWeb',
        matches: ['/CRMEnquiryWebDetails'],
        countKey: 'inquiry_email_count',
        // The sheet names this one "View Enquiry Email": it is the email
        // enquiry queue (api/email_inquiry_list), which the web labels Web.
        permission: CRM_PERM.VIEW_ENQUIRY_EMAIL,
      },
      {
        label: 'Whatsapp',
        to: '/CRMEnquiryWhatsapp',
        matches: ['/CRMWhatsappChat'],
        countKey: 'inquiry_whatsapp_count',
        permission: CRM_PERM.VIEW_ENQUIRY_WHATSAPP,
      },
      // Chat Bot tab hidden for now - the screen and its route stay in place,
      // this just takes it off the Enquiry tab strip (the matching dashboard
      // tile is commented out in CRMAdminDashboard.js the same way).
      // {
      //   label: 'Chat Bot',
      //   to: '/CRMEnquiryChatBot',
      //   matches: ['/CRMWebChat'],
      //   countKey: 'chat_bot_inquiry_count',
      //   permission: CRM_PERM.VIEW_ENQUIRY_CHAT_BOT,
      // },
    ],
  },
  {
    key: 'prospect',
    label: 'Prospect',
    icon: 'prospect',
    permission: CRM_PERM.VIEW_PROSPECT,
    tabs: [
      {
        label: 'Call',
        to: '/CRMProspectCall',
        countKey: 'prospect_call_count',
        permission: CRM_PERM.VIEW_PROSPECT_CALL,
      },
      {
        label: 'Web',
        to: '/CRMProspectWeb',
        matches: ['/CRMProspectDetails'],
        countKey: 'prospect_email_count',
        // "View Prospect Email" on the sheet - same tab, the email queue.
        permission: CRM_PERM.VIEW_PROSPECT_EMAIL,
      },
      {
        label: 'Whatsapp',
        to: '/CRMProspectWhatsapp',
        countKey: 'prospect_whatsapp_count',
        permission: CRM_PERM.VIEW_PROSPECT_WHATSAPP,
      },
      // Chat Bot tab hidden for now - see the note on the Enquiry group above.
      // {
      //   label: 'Chat Bot',
      //   to: '/CRMProspectChatBot',
      //   matches: ['/CRMProspectBotDetails'],
      //   countKey: 'prospect_chat_bot_count',
      //   permission: CRM_PERM.VIEW_PROSPECT_CHAT_BOT,
      // },
      {
        label: 'Registered',
        to: '/CRMProspectRegistered',
        countKey: 'registered_users_count',
        permission: CRM_PERM.VIEW_REGISTERED,
      },
    ],
  },
  {
    key: 'customer',
    label: 'Customer',
    icon: 'customer',
    permission: CRM_PERM.VIEW_CUSTOMER,
    tabs: [
      {
        label: 'Call',
        to: '/CRMCustomerCall',
        countKey: 'customer_call_count',
        permission: CRM_PERM.VIEW_CUSTOMER_CALL,
      },
      {
        label: 'Web',
        to: '/CRMCustomerWeb',
        countKey: 'customer_email_count',
        permission: CRM_PERM.VIEW_CUSTOMER_WEB,
      },
      {
        label: 'Whatsapp',
        to: '/CRMCustomerWhatsapp',
        countKey: 'customer_whatsapp_count',
        permission: CRM_PERM.VIEW_CUSTOMER_WHATSAPP,
      },
    ],
  },
  {
    key: 'outgoing',
    label: 'Outgoing - Sales',
    icon: 'outgoing',
    permission: CRM_PERM.VIEW_OUTGOING_SALES,
    tabs: [
      // Bulk Call / Whatsapp Promotion / SMS Promotion — taken out of the
      // strip for now, kept verbatim so they can go straight back in. Their
      // routes are still registered in App.js, so a direct link still works;
      // ROUTE_PERMISSIONS below gates them meanwhile, and their sheet
      // permissions are written in here so uncommenting restores them wired.
      // { label: 'Bulk Call', to: '/CRMOutgoingBulkCall', permission: CRM_PERM.VIEW_OUTGOING_SALES_BULK_CALL },
      // {
      //   label: 'Whatsapp Promotion',
      //   to: '/CRMOutgoingWhatsappPromotion',
      //   matches: ['/CRMOutgoingWhatsappPromotionCreate', '/CRMOutgoingPromotionDetails', '/CRMOutgoingCustomGroup'],
      //   permission: CRM_PERM.VIEW_WHATSAPP_PROMOTION,
      // },
      // {
      //   label: 'SMS Promotion',
      //   to: '/CRMOutgoingSmsPromotion',
      //   matches: ['/CRMOutgoingSmsPromotionCreate'],
      //   permission: CRM_PERM.VIEW_SMS_PROMOTION,
      // },
      {
        label: 'Push Notification',
        to: '/CRMOutgoingPushNotification',
        matches: ['/CRMOutgoingPushNotificationCreate', '/CRMOutgoingPushNotificationDetails'],
        permission: CRM_PERM.VIEW_PUSH_NOTIFICATION,
      },
      {
        label: 'Push Groups',
        to: '/PushGroups',
        matches: ['/PushGroups/create', '/CRMOutgoingPushGroups'],
        permission: CRM_PERM.VIEW_PUSH_GROUPS,
      },
      {
        label: 'Stories & Updates',
        to: '/CRMOutgoingStories',
        matches: ['/CRMOutgoingStoryCreate'],
        permission: CRM_PERM.VIEW_STORIES_AND_UPDATES,
      },
      {
        label: 'Email Promotion',
        to: '/CRMOutgoingEmailPromotion',
        hidden: true,
        permission: CRM_PERM.VIEW_EMAIL_PROMOTION,
      },
      // Not on the sheet — mobile has no equivalent, so it stays ungated.
      { label: 'Offers & Deals', to: '/CRMOutgoingOffers', matches: ['/CRMOutgoingOfferCreate'], hidden: true },
    ],
  },
  // General Reports — the whole sub-module is out of the sidebar and the tab
  // strip for now. Kept verbatim rather than deleted; uncommenting this one
  // object brings the group back exactly as it was (the three `hidden` tabs
  // included, which are the ones mobile does not offer). Every route below is
  // still registered in App.js, so a saved link still opens the screen — it
  // just renders without the CRM group frame while this is commented out.
  // The sheet's permissions (rows 44-51) are written in below so that
  // uncommenting restores the group already gated; until then every one of
  // these routes is gated through ROUTE_PERMISSIONS instead.
  // {
  //   key: 'reports',
  //   label: 'General Reports',
  //   icon: 'reports',
  //   permission: CRM_PERM.VIEW_GENERAL_REPORT,
  //   tabs: [
  //     { label: 'Stats', to: '/CRMReportsStats', permission: CRM_PERM.VIEW_STATS },
  //     { label: 'Activity Log', to: '/CRMReportsActivityLog', permission: CRM_PERM.VIEW_ACTIVITY_LOG },
  //     { label: 'Call Report', to: '/CRMReportsCallReview', matches: ['/CRMReportsTotalCallReport'], permission: CRM_PERM.VIEW_CALL_REPORT },
  //     { label: 'Channel Report', to: '/CRMReportsChannelReport', permission: CRM_PERM.VIEW_CHANNEL_REPORT },
  //     { label: 'Call Summary', to: '/CRMReportsCallSummary', hidden: true, permission: CRM_PERM.VIEW_CALL_SUMMARY },
  //     { label: 'Registered Users', to: '/CRMReportsRegisteredUsers', hidden: true, permission: CRM_PERM.VIEW_REGISTERED_USERS },
  //     { label: 'Whatsapp Report', to: '/CRMReportsWhatsapp', hidden: true, permission: CRM_PERM.VIEW_WHATSAPP_REPORT },
  //   ],
  // },
  {
    key: 'contacts',
    label: 'Contacts',
    icon: 'contacts',
    // The sheet gives Contacts no group-level permission of its own (rows
    // 52-53 are the two tabs), so the group is as visible as its tabs.
    tabs: [
      {
        label: 'Contact Management',
        to: '/CRMDrawerContactManagement',
        matches: ['/CRMContactDetails', '/CRMContactEdit', '/CRMContactProfile'],
        permission: CRM_PERM.VIEW_CONTACT_MANAGEMENT,
      },
      { label: 'Contact Search', to: '/CRMDrawerContactSearch', permission: CRM_PERM.VIEW_CONTACT_SEARCH },
      // Dialer taken out of the Contacts strip: the native app has no such
      // tab. Its dial pad is a floating button on the OfficeCaller home
      // (Admin.js, gated by the 'View Dial Pad' right), not part of the
      // Contacts submodule. /CRMDialer stays registered in App.js so a
      // direct link still opens it.
      // { label: 'Dialer', to: '/CRMDialer' },
    ],
  },
  {
    key: 'hr',
    label: 'HR',
    icon: 'hr',
    permission: CRM_PERM.VIEW_HR,
    tabs: [
      { label: 'HR Dashboard', to: '/CRMHRDashboard', permission: CRM_PERM.VIEW_HR_DASHBOARD },
      {
        label: 'Candidate',
        to: '/CRMHRCandidate',
        matches: ['/CRMHRCandidateDetails'],
        permission: CRM_PERM.VIEW_CANDIDATE,
      },
      { label: 'Interested Candidate', to: '/CRMHRInterestedCandidate', permission: CRM_PERM.VIEW_INTERESTED_CANDIDATE },
      { label: 'Job Applicant', to: '/CRMHRJobApplicant', permission: CRM_PERM.VIEW_JOB_APPLICANT },
      // HR's own promotion screens — distinct names from the Outgoing - Sales
      // ones (rows 59-60 vs 33/35), though both render PromotionListScreen.
      { label: 'Whatsapp Promotion', to: '/CRMHRPromotionWhatsapp', permission: CRM_PERM.VIEW_HR_WHATSAPP_PROMOTION },
      { label: 'SMS Promotion', to: '/CRMHRPromotionSms', permission: CRM_PERM.VIEW_HR_SMS_PROMOTION },
    ],
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: 'notifications',
    tabs: [
      { label: 'Overview', to: '/CRMNotifications' },
      { label: 'Leads', to: '/CRMNotificationsLeads' },
      { label: 'Live Call', to: '/CRMNotificationsLiveCall' },
      { label: 'Frequent Call', to: '/CRMNotificationsFrequentCall' },
      { label: 'Whatsapp', to: '/CRMNotificationsWhatsapp' },
      { label: 'Email Enquiry', to: '/CRMNotificationsEmailEnquiry' },
      { label: 'Call Review', to: '/CRMNotificationsCallReview' },
    ],
  },
  {
    key: 'add',
    label: 'Add Lead',
    icon: 'add',
    // No gate of its own - the group is exactly as visible as the forms left
    // in it, and allowedGroups() folds it away once they all filter out.
    tabs: [
      { label: 'Add Lead', to: '/CRMAddLead', permission: CRM_PERM.ADD_LEADS },
      { label: 'Add Lead (Hiring Team)', to: '/CRMAddLeadHiring', permission: CRM_PERM.ADD_LEADS },
      { label: 'Add Prospect', to: '/CRMAddProspect', permission: CRM_PERM.ADD_AS_PROSPECT },
      { label: 'Job Applicant', to: '/CRMAddCandidate', permission: CRM_PERM.ADD_AS_CANDIDATE },
      { label: 'Add Consultant', to: '/CRMAddConsultant', permission: CRM_PERM.ADD_CONSULTANT },
      { label: 'Add Supplier', to: '/CRMAddSupplier', permission: CRM_PERM.ADD_SUPPLIER },
    ],
  },
];

/**
 * Permissions for routes that are not tabs of their own — the detail screens
 * listed under a tab's `matches`. Contact Profile is reached from a dozen
 * places (Recent Calls, Contact Search, every "Unified History" tile, the
 * notification lists), so the sheet's "View Contact Profile" is enforced on
 * the screen itself rather than on each of those buttons.
 */
export const ROUTE_PERMISSIONS = {
  '/CRMContactProfile': CRM_PERM.VIEW_CONTACT_PROFILE,

  // Outgoing - Sales tabs that are commented out of the strip above. Their
  // routes stay registered in App.js, so without these a direct link would
  // open them ungated.
  '/CRMOutgoingBulkCall': CRM_PERM.VIEW_OUTGOING_SALES_BULK_CALL,
  '/CRMOutgoingWhatsappPromotion': CRM_PERM.VIEW_WHATSAPP_PROMOTION,
  '/CRMOutgoingSmsPromotion': CRM_PERM.VIEW_SMS_PROMOTION,

  // The whole General Report group is commented out, so resolveNav finds no
  // group for these — rows 44-51 are enforced per route instead.
  '/CRMReportsStats': CRM_PERM.VIEW_STATS,
  '/CRMReportsActivityLog': CRM_PERM.VIEW_ACTIVITY_LOG,
  '/CRMReportsCallReview': CRM_PERM.VIEW_CALL_REPORT,
  '/CRMReportsTotalCallReport': CRM_PERM.VIEW_CALL_REPORT,
  '/CRMReportsChannelReport': CRM_PERM.VIEW_CHANNEL_REPORT,
  '/CRMReportsCallSummary': CRM_PERM.VIEW_CALL_SUMMARY,
  '/CRMReportsRegisteredUsers': CRM_PERM.VIEW_REGISTERED_USERS,
  '/CRMReportsWhatsapp': CRM_PERM.VIEW_WHATSAPP_REPORT,

  // Create screens (rows 39-43). These sit under a tab's `matches`, so the
  // tab's View permission already gates them; the Create name is the extra
  // one the sheet asks for. The list screens hide their "Create" buttons on
  // the same name — this is the guard for a pasted link.
  '/CRMOutgoingPushNotificationCreate': CRM_PERM.CREATE_PUSH_NOTIFICATION,
  '/CRMOutgoingStoryCreate': CRM_PERM.CREATE_STORIES_AND_UPDATES,
  '/CRMOutgoingSmsPromotionCreate': CRM_PERM.CREATE_SMS_PROMOTION,
};

/** The six "create" forms — offered from the "+ Add" menu on every CRM page. */
export const ADD_MENU = CRM_NAV.find((g) => g.key === 'add').tabs;

const norm = (p) => (p && p.length > 1 ? p.replace(/\/+$/, '') : p || '');

/** The tabs a group shows in its strip — everything not marked `hidden`. */
export const visibleTabs = (group) => (group?.tabs || []).filter((t) => !t.hidden);

/**
 * The tabs this user may actually open: the visible ones, minus any whose own
 * `permission` they lack — and none at all when the group's own permission is
 * denied, since the sheet nests the tab permissions under the group one
 * ("View Prospect" > "View Prospect Call"). `can` is the checker from
 * usePermissions / the sidebar; passing none leaves the list ungated, which
 * is what a screen that does not care about permissions gets.
 */
export const allowedTabs = (group, can) => {
  if (!group) return [];
  if (group.permission && can && !can(group.permission)) return [];
  return visibleTabs(group).filter((t) => !t.permission || (can ? can(t.permission) : true));
};

/**
 * The CRM groups this user may see — those with at least one tab left after
 * filtering. A group whose every tab is denied, or whose own permission is,
 * is a row that leads nowhere, so it folds away rather than landing on the
 * no-access panel.
 */
export const allowedGroups = (groups, can) =>
  (groups || []).filter((g) => allowedTabs(g, can).length > 0);

/** Every route a group owns (tab routes plus their detail / create routes). */
export const groupPaths = (group) => group.tabs.flatMap((t) => [t.to, ...(t.matches || [])]);

/** Every CRM Admin route — used for the sidebar section's "active" state. */
export const ALL_CRM_PATHS = CRM_NAV.flatMap(groupPaths);

/**
 * Where a pathname sits: its group, the tab it belongs to, and whether it is
 * a detail / create page under that tab (so CrmPage can show a back button).
 */
export const resolveNav = (pathname) => {
  const p = norm(pathname);
  for (const group of CRM_NAV) {
    for (const tab of group.tabs) {
      if (norm(tab.to) === p) return { group, tab, isDetail: false };
      if ((tab.matches || []).some((m) => norm(m) === p)) return { group, tab, isDetail: true };
    }
  }
  return { group: null, tab: null, isDetail: false };
};
